import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import TuneIcon from '@mui/icons-material/Tune';

import {
  DEFAULT_FILTERS_STATE,
  isDefault,
  resetFilters,
  type FiltersState,
} from './filterState';

/**
 * Open/closed state is persisted per browser so the user's preference
 * survives reloads. Defaults closed — the elevation pass 2026-05-26 made
 * Filters opt-in so the Styles palette (visually richer, especially once
 * swatch imagery lands) keeps its share of the rail.
 */
const FILTERS_OPEN_STORAGE_KEY = 'ct_v3_filters_panel_open';

function loadFiltersOpen(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(FILTERS_OPEN_STORAGE_KEY);
    return raw === '1';
  } catch {
    return false;
  }
}

function saveFiltersOpen(open: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(FILTERS_OPEN_STORAGE_KEY, open ? '1' : '0');
  } catch {
    // Best-effort — localStorage isn't critical for this panel.
  }
}

interface Props {
  state: FiltersState;
  onChange: (next: FiltersState) => void;
  /** Bake the previewed filters into a new asset via the upload pipeline.
   *  Bucket 5. When undefined the button hides — keeps FilterPanel usable
   *  in contexts where there's no active image (e.g. tests). */
  onApply?: () => Promise<void>;
  /** True when there's no source image to bake against; the panel still
   *  renders sliders for the preview-when-there-is-one case but disables
   *  the Apply button with a tooltip explaining why. */
  hasSourceImage?: boolean;
}

interface SliderSpec {
  key: keyof FiltersState;
  label: string;
  min: number;
  max: number;
  step: number;
  default: number;
  unit: string;
}

const SLIDERS: SliderSpec[] = [
  { key: 'brightness', label: 'Brightness', min: 0, max: 200, step: 1, default: 100, unit: '%' },
  { key: 'contrast', label: 'Contrast', min: 0, max: 200, step: 1, default: 100, unit: '%' },
  { key: 'saturation', label: 'Saturation', min: 0, max: 200, step: 1, default: 100, unit: '%' },
  { key: 'hue', label: 'Hue', min: -180, max: 180, step: 1, default: 0, unit: '°' },
  { key: 'blur', label: 'Blur', min: 0, max: 10, step: 0.1, default: 0, unit: 'px' },
  { key: 'grayscale', label: 'Grayscale', min: 0, max: 100, step: 1, default: 0, unit: '%' },
  { key: 'sepia', label: 'Sepia', min: 0, max: 100, step: 1, default: 0, unit: '%' },
  { key: 'invert', label: 'Invert', min: 0, max: 100, step: 1, default: 0, unit: '%' },
];

/**
 * Client-side CSS-filter preview panel. Lives in the left rail below the
 * SwatchPanel. Each slider drives one dimension of `FiltersState`; the
 * shell composes the CSS string via `filtersToCss` and applies it to the
 * canvas container. These adjustments are pure preview — they don't
 * mutate the underlying asset until the user runs an operation that
 * bakes them (planned: an "Apply filters" button that materializes a
 * new asset via the ColorCorrect / Enhance op pipeline).
 */
export const FilterPanel: React.FC<Props> = ({ state, onChange, onApply, hasSourceImage = true }) => {
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  // Collapse state — defaults closed; persists via localStorage. Elevation
  // pass 2026-05-26.
  const [open, setOpen] = useState<boolean>(() => loadFiltersOpen());
  useEffect(() => {
    saveFiltersOpen(open);
  }, [open]);
  const modifiedCount = SLIDERS.reduce(
    (n, spec) => (state[spec.key] !== spec.default ? n + 1 : n),
    0,
  );

  const handleSlider = (key: keyof FiltersState, value: number) => {
    onChange({ ...state, [key]: value });
  };

  const handleReset = () => onChange(resetFilters());

  const handleDouble = (key: keyof FiltersState, spec: SliderSpec) => () => {
    onChange({ ...state, [key]: spec.default });
  };

  const dirty = !isDefault(state);

  const handleApply = async () => {
    if (!onApply) return;
    setApplying(true);
    setApplyError(null);
    try {
      await onApply();
    } catch (err) {
      setApplyError(err instanceof Error ? err.message : 'Failed to apply filters.');
    } finally {
      setApplying(false);
    }
  };

  const applyDisabled = applying || !dirty || !hasSourceImage || !onApply;
  const applyTooltip = !onApply
    ? 'Apply Filters becomes available when the active image is loaded'
    : !hasSourceImage
      ? 'No image to apply filters to'
      : !dirty
        ? 'Drag a slider to change the preview before applying'
        : 'Bake the previewed filters into a new asset';

  return (
    <Box sx={{ p: 1.5 }} aria-label="Filter panel">
      {/* Header row — title + summary on the left, reset + collapse toggle
          on the right. The chevron IconButton is the single, keyboard-
          accessible toggle (a duplicate header click target produced two
          buttons with the same aria-label, breaking unique-name a11y). */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ userSelect: 'none', flex: 1, minWidth: 0 }}
        >
          <TuneIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography variant="overline" color="text.secondary">
            Filters
          </Typography>
          {!open && modifiedCount > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              · {modifiedCount} active
            </Typography>
          )}
          {!open && modifiedCount === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              · neutral
            </Typography>
          )}
        </Stack>
        <Stack direction="row" spacing={0.25} alignItems="center">
          <Tooltip title={dirty ? 'Reset filters to neutral' : 'No active filters'}>
            <span>
              <IconButton
                size="small"
                onClick={handleReset}
                disabled={!dirty}
                aria-label="Reset filters"
              >
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={open ? 'Collapse Filters' : 'Expand Filters'}>
            <IconButton
              size="small"
              onClick={() => setOpen(o => !o)}
              aria-label={open ? 'Collapse Filters' : 'Expand Filters'}
              aria-expanded={open}
            >
              {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Collapse in={open} unmountOnExit={false}>
      <Stack spacing={1.25}>
        {SLIDERS.map(spec => {
          const value = state[spec.key];
          const isModified = value !== spec.default;
          return (
            <Box key={spec.key}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.25 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: isModified ? 'primary.main' : 'text.secondary',
                    fontWeight: isModified ? 500 : 400,
                    cursor: 'pointer',
                  }}
                  onDoubleClick={handleDouble(spec.key, spec)}
                  title="Double-click label to reset this slider"
                >
                  {spec.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatValue(value, spec)}
                </Typography>
              </Stack>
              <Slider
                size="small"
                value={value}
                min={spec.min}
                max={spec.max}
                step={spec.step}
                onChange={(_, v) => handleSlider(spec.key, typeof v === 'number' ? v : v[0])}
                aria-label={spec.label}
                sx={{ py: 0.5 }}
              />
            </Box>
          );
        })}
      </Stack>

      <Tooltip title={applyTooltip}>
        <span>
          <Button
            size="small"
            variant="outlined"
            disabled={applyDisabled}
            fullWidth
            sx={{ mt: 1.5, fontSize: '0.75rem' }}
            startIcon={applying ? <CircularProgress size={14} /> : <SaveAltIcon fontSize="small" />}
            onClick={handleApply}
          >
            {applying ? 'Applying…' : 'Apply Filters'}
          </Button>
        </span>
      </Tooltip>

      {applyError && (
        <Alert severity="warning" sx={{ mt: 1 }} onClose={() => setApplyError(null)}>
          {applyError}
        </Alert>
      )}
      </Collapse>
    </Box>
  );
};

function formatValue(value: number, spec: SliderSpec): string {
  if (spec.unit === 'px') return `${value.toFixed(1)} ${spec.unit}`;
  if (spec.unit === '°') return `${value >= 0 ? '+' : ''}${value}${spec.unit}`;
  return `${Math.round(value)}${spec.unit}`;
}

export { DEFAULT_FILTERS_STATE };
